import {action, autorun, observable, reaction, toJS} from 'mobx';
import {env} from 'next-runtime-env';
import userApi from '@utils/api/user.api';
import gatewayApi from '@utils/api/gateway.api';
import {isEmpty} from 'lodash-es';
import {Currency} from '@interface';
import BaseStore from './baseStore';
import SessionStore from './session';
import * as gameRoomInterface from 'poker-interfaces/game-room/game-room-interface';
import SystemStore from './system';
import LanguageStore from './language';
import Router from 'next/router';
import authApi from '@utils/api/auth.api';
import {notification} from 'antd';
import {disconnect} from '@wagmi/core';
import WalletConnectConfig from '@configs/WalletConnectConfig';
import {clearLocalStorageExceptDontShowDisclaimerAgain} from '..';
import {isTelegramMiniApp} from '@components/layout';

const NEXT_PUBLIC_URL = env('NEXT_PUBLIC_URL');
class UserStore extends BaseStore {
  @observable info: any = {};

  @observable playerInfo: any = {};

  @observable playerThirdPartyInfo: any = {};

  @observable playerIconUrl: string;

  @observable playerToken: string = null;

  @observable joinedRooms: gameRoomInterface.JoinedRoomProps[] = [];

  // @observer theme='purple'  *use enum
  @observable currentTheme = 'purple';

  @observable registrationType: string;

  @observable isTelegramBrowser: boolean = false;

  @observable isComeFromThirdParty: boolean = false;

  sessionStore: SessionStore;

  system: SystemStore;

  languageStore: LanguageStore;

  constructor(sessionStore, system, languageStore) {
    super();
    this.isReady = true;
    this.sessionStore = sessionStore;
    this.system = system;
    this.languageStore = languageStore;
    this.playerToken = this.getPlayerToken();
    this.joinedRooms = [];

    reaction(
      () => this.playerToken,
      async () => {
        if (this.playerToken && !isTelegramMiniApp) {
          // if (response) {
          //   this.setPlayerInfo(response);
          // }
          const response = await this.getPlayerInfo();
          // console.log(' @@ reaction - response:', response);
          if (!response) {
            console.log(' !! no resp => handle empty player info');
            this.handleEmptyPlayerInfo();
          } else if (response) {
            this.setPlayerInfo(response);
          }
          // Only refresh third party info if NOT from a third-party gateway
          if (!this.isThirdPartyGateway()) {
            this.refreshPlayerThirdPartyInfo();
          }
        } else if (isTelegramMiniApp) {
          //  * this part is for getting the joined room after refresh
          // console.log(' ** isTelegramMiniApp - this.playerToken:', this.playerToken);
          const isTokenUpdated = sessionStorage.getItem('isTokenUpdated') === 'true'; // Check if token is updated

          // // Call `getPlayerInfo` if `isTokenUpdated` is true
          if (isTokenUpdated) {
            // * for getting the joined rooms after refresh
            // * 'please log in again' err if using old token
            // notification.info({
            //   message: '3. after refresh',
            //   description: this.playerToken,
            // });
            const response = await this.getPlayerInfo();
            if (!response) {
              notification.info({
                message: '4. after refresh - !response',
                description: JSON.stringify(response),
              });

              if (isTelegramMiniApp) {
                localStorage.removeItem('playerToken');
                this.sessionStore.removeSession();
                this.sessionStore.clearCookie();
                this.info = {};
                Router.replace('/telegram');
              } else {
                this.handleEmptyPlayerInfo();
              }
            } else {
              this.setPlayerInfo(response);
            }
          }
        }
      },
      {fireImmediately: true}
    );
  }

  @action isLogin() {
    return Boolean(this.getPlayerToken());
  }

  @action async getPersonalInfo() {
    const response = await userApi.getPersonalInfo();
    this.info = response;
  }

  @action async getPlayerInfo() {
    try {
      const response = await userApi.getPlayerInfo(this.playerToken);

      return response;
    } catch (error) {
      console.log(' getPlayerInfo - error', error?.response);
      return null;
    }
  }

  @action setIsTelegramBrowser(isTg: boolean) {
    this.isTelegramBrowser = isTg;
  }

  @action setIsComeFromThirdParty(isThirdParty: boolean) {
    this.isComeFromThirdParty = isThirdParty;
    window.localStorage.setItem('isComeFromThirdParty', isThirdParty.toString());

    if (isThirdParty && !window.localStorage.getItem('pj_merchant_open_id')) {
      window.localStorage.removeItem('gatewayToken');
    }
  }

  @action getIsComeFromThirdParty() {
    const identifiedFromThirdParty =
      this.isComeFromThirdParty ||
      window.localStorage.getItem('isComeFromThirdParty') === 'true' ||
      this.playerInfo?.uiMode === 'SIMPLIFIED';

    // always set the flag to true if identified from third party
    if (identifiedFromThirdParty) {
      this.setIsComeFromThirdParty(true);
      window.localStorage.setItem('isComeFromThirdParty', 'true');
    }

    return identifiedFromThirdParty;
    // return (
    //   this.isComeFromThirdParty ||
    //   window.localStorage.getItem('isComeFromThirdParty') === 'true' ||
    //   this.isThirdPartyGateway()
    // );
  }

  @action isThirdPartyGateway() {
    // return false;
    if (!this.isLogin()) {
      // To display login buttons when not logged in
      return false;
    }

    // always set the flag to true if identified from third party
    const identifiedFromThirdParty =
      this.isComeFromThirdParty ||
      window.localStorage.getItem('isComeFromThirdParty') === 'true' ||
      this.playerInfo?.uiMode === 'SIMPLIFIED';

    // always set the flag to true if identified from third party
    if (identifiedFromThirdParty) {
      this.setIsComeFromThirdParty(true);
      window.localStorage.setItem('isComeFromThirdParty', 'true');
    }

    return identifiedFromThirdParty;

    //return this.playerInfo?.uiMode === 'SIMPLIFIED' || this.isComeFromThirdParty;
  }

  @action async refreshPlayerInfo() {
    try {
      // notification.info({
      //   message: '2. refreshPlayerInfo',
      //   description: this.playerToken,
      // });
      const response = await this.getPlayerInfo();

      console.log(' * refreshPlayerInfo - response ', response);
      this.setPlayerInfo(response);

      return Promise.resolve(null);
    } catch (e) {
      console.log('refreshPlayerInfo error', e);
      return Promise.reject(e);
    }
  }

  @action setPlayerInfo(response) {
    if (this.playerInfo != response) {
      this.playerInfo = response;
    }

    if (response) {
      if (!response?.icon) {
        this.playerIconUrl = null;
      } else if (`${NEXT_PUBLIC_URL}${response?.icon}` !== this.playerIconUrl) {
        this.playerIconUrl = `${NEXT_PUBLIC_URL}${response?.icon}`;
      }
      if (JSON.stringify(this.joinedRooms) != response.joinedRooms) {
        this.joinedRooms = response.joinedRoom;
      }
    }
  }

  @action async getPlayerThirdPartyInfo() {
    const response = await authApi.postPlayerInfo({accessToken: this.playerToken});
    return response;
  }

  @action async setPlayerThirdPartyInfo(response) {
    this.playerThirdPartyInfo = response?.data;
  }

  @action async refreshPlayerThirdPartyInfo() {
    try {
      const response = await this.getPlayerThirdPartyInfo();
      // console.log(' ## refreshPlayerThirdPartyInfo - response', response);
      this.setPlayerThirdPartyInfo(response);
    } catch (e) {
      console.log(' @@ refreshPlayerThirdPartyInfo - error', e?.response);
      console.log(' @@ refreshPlayerThirdPartyInfo - error', e?.response?.status);
      console.log(' @@ refreshPlayerThirdPartyInfo - error', e?.response?.status === 401);

      // eslint-disable-next-line default-case
      switch (e?.response?.status) {
        case 401:
          if (isTelegramMiniApp) {
            localStorage.removeItem('playerToken');
            this.sessionStore.removeSession();
            this.sessionStore.clearCookie();
            this.info = {};
            Router.replace('/telegram');
          } else {
            localStorage.removeItem('playerToken');
            this.sessionStore.removeSession();
            this.sessionStore.clearCookie();
            this.info = {};
            window.location.reload();
          }
          break;
      }
    }
  }

  @action getPlayerId() {
    if (!this.playerInfo) return null;
    return this.playerInfo.id;
  }

  @action async updateTwoFactor(token) {
    const {twoFAEnable} = this.info;
    await userApi.updateTwoFactor(token, !twoFAEnable);
    await this.getPersonalInfo();
  }

  @action async login(userName, agent) {
    const response = await userApi.loginCms(userName, agent);
    const {accessToken, refreshToken} = response;
    this.sessionStore.setSession(accessToken, refreshToken);
  }

  @action async socialLogin(token, type) {
    const response = await userApi.socialLogin(token, type);
    const {accessToken, refreshToken} = response;
    this.sessionStore.setSession(accessToken, refreshToken);
  }

  @action updatePlayerToken(token: string, isFromThirdParty: boolean = false) {
    console.log('mobx - updatePlayerToken - token', token);
    window.localStorage.setItem('playerToken', token);
    this.playerToken = token;
    // Reset the third-party flag unless explicitly set to true (for login-with-token)
    if (!isFromThirdParty) {
      this.setIsComeFromThirdParty(false);
      window.localStorage.removeItem('isComeFromThirdParty');
    } else {
      this.setIsComeFromThirdParty(true);
      window.localStorage.setItem('isComeFromThirdParty', 'true');
    }
  }

  @action getPlayerToken() {
    if (typeof window === 'undefined') return null;
    const overrideToken = localStorage.getItem('playerTokenOverride');
    if (overrideToken !== null && overrideToken !== '') {
      return localStorage.getItem('playerTokenOverride');
    }
    return localStorage.getItem('playerToken');
  }

  @action getPlayerIconUrl() {
    if (!this.playerInfo?.icon) return null;
    return `${NEXT_PUBLIC_URL}${this.playerInfo.icon}`;
  }

  @action async logout() {
    await userApi.logout();
    this.sessionStore.removeSession();
    this.sessionStore.clearCookie();
    this.info = {};
    this.setIsComeFromThirdParty(false); // Reset third-party flag on logout
  }

  @action handleEmptyPlayerInfo = async () => {
    this.updatePlayerToken('');
    this.setIsComeFromThirdParty(false); // Reset third-party flag
    clearLocalStorageExceptDontShowDisclaimerAgain();
    notification.info({
      message:
        // TODO fix this for telegram
        this.languageStore?.currentLanguage === 'en-US' ? 'Please log in again' : '請重新登入',
    });
    this.system?.showModalSheet('login');
    try {
      await disconnect(WalletConnectConfig);
    } catch (error) {
      console.log('error', error);
    }
  };

  @action getCoinWalletBalance(): number {
    if (isEmpty(this.playerInfo)) return 0;
    return this.playerInfo?.coinWalletBalance;
  }

  @action getDemoCoinWalletBalance(): number {
    if (isEmpty(this.playerInfo)) return 0;
    return this.playerInfo?.demoCoinWalletBalance;
  }

  @action getDiamondWalletBalance(): number {
    if (isEmpty(this.playerInfo)) return 0;
    return this.playerInfo?.diamondWalletBalance;
  }

  @action getPlayerTotalWinAmount(): number {
    if (isEmpty(this.playerInfo)) return 0;
    return this.playerInfo?.totalWinAmount;
  }

  @action getPlayerDisplayName(): string {
    if (isEmpty(this.playerInfo)) return '';
    return this.playerInfo.displayName;
  }

  @action appendJoinedRooms(room: gameRoomInterface.JoinedRoomProps): void {
    this.joinedRooms.push(room);
  }

  @action async removeJoinedRoom(roomCode: string) {
    this.joinedRooms = this.joinedRooms.filter(room => room.roomCode !== roomCode);
    this.handleJoinedRoomUpdate();
  }

  @action async refreshJoinedRoom(isAutoSetIndex?: boolean) {
    try {
      await this.refreshPlayerInfo();
      this.handleJoinedRoomUpdate(isAutoSetIndex);
    } catch (e) {
      console.log('refreshJoinedRoom error', e);
    }
  }

  // ! conflicts with loading page
  @action handleJoinedRoomUpdate(isAutoSetIndex: boolean = true) {
    // console.log(' ** handleJoinedRoomUpdate', toJS(this.joinedRooms));
    if (this.joinedRooms?.length === 0) {
      // Router.push('/home');
      this.system.hideGameOverlay();
    } else if (isAutoSetIndex) {
      this.system.setActiveTableIndex(this.joinedRooms.length - 1);
    }
  }

  @action deposit({
    value,
    gatewayUserName,
    playerToken,
  }: {
    value: number;
    gatewayUserName?: string;
    playerToken?: string;
  }): Promise<any> {
    return gatewayApi.deposit(
      value,
      gatewayUserName || this.playerInfo.gatewayUserName,
      playerToken || this.playerToken
    );
  }

  @action getDemoProgress() {
    return this.playerInfo?.demoCoinQualifyCount || 0;
  }

  @action isQualified() {
    return this.playerInfo?.isDemoCoinClaimed || 0;
  }

  @action setRegistrationType(type: string) {
    this.registrationType = type;
  }
}

export default UserStore;
